import { PATH_DASHBOARD } from '@/app-routes';
import { Component, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { BreadcrumbService } from './services/breadcrumb.service';
import { hasItemsGreaterThanZero } from '@/core/utils/functions';
import { BreadcrumbEventModel } from './models/breadcrumb-event.model';
import { CookieService } from 'ngx-cookie-service';
import { SessionService } from '@/core/services/security/session.service';
import { FavoriteMenuService } from '@/core/services/security/favorite-menu.service';
import { MessagesUtils } from '@/core/utils/layout/messages.utils';

@Component({
    selector: '[app-breadcrumb]',
    templateUrl: './breadcrumb.component.html',
    styleUrls: ['./breadcrumb.component.scss'],
    standalone: false,
    host: {
        class: 'layout-breadcrumb-container'
    }
})
export class BreadcrumbComponent implements OnDestroy {
    private listBreadcrumbs: Breadcrumb[] = [];
    private readonly _breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);
    readonly breadcrumbs$ = this._breadcrumbs$.asObservable();
    private routerSubscription?: Subscription;
    private favoritosSubscription?: Subscription;

    pathHome: string = PATH_DASHBOARD;
    favoriteIcon: string = 'pi pi-fw pi-star';

    constructor(
        private router: Router,
        private service: BreadcrumbService,
        private sessionService: SessionService,
        private cookieService: CookieService,
        private favoriteMenuService: FavoriteMenuService,
        private messagesUtils: MessagesUtils,
    ) {
        this.routerSubscription = this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe(() => {
                const root = this.router.routerState.snapshot.root;
                const breadcrumbs: Breadcrumb[] = [];
                this.addBreadcrumb(root, [], breadcrumbs);

                this._breadcrumbs$.next(breadcrumbs);
                this.listBreadcrumbs = breadcrumbs;
                this.favoriteIcon = this.getInitialStateFavoriteIcon();
            });

        this.favoritosSubscription = this.favoriteMenuService.favoritos$.subscribe(() => {
            this.favoriteIcon = this.getInitialStateFavoriteIcon();
        });
    }

    ngOnDestroy(): void {
        this.routerSubscription?.unsubscribe();
        this.favoritosSubscription?.unsubscribe();
    }

    private addBreadcrumb(route: ActivatedRouteSnapshot, parentUrl: string[], breadcrumbs: Breadcrumb[]) {
        const routeUrl = parentUrl.concat(route.url.map((url) => url.path));
        const breadcrumb = route.data['breadcrumb'];
        const favoriteId = route.data['favoriteId'];
        const cardInfoId = route.data['cardInfoId'];
        const role = route.data['role'];
        const parentBreadcrumb = route.parent && route.parent.data ? route.parent.data['breadcrumb'] : null;

        if (breadcrumb && breadcrumb !== parentBreadcrumb) {
            breadcrumbs.push({
                label: breadcrumb,
                url: '/' + routeUrl.join('/'),
                favoriteId,
                cardInfoId,
                role,
            });
        }

        if (route.firstChild) {
            this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs);
        }
    }

    favoriteOnAction() {
        const current = this.getCurrentBreadcrumb();
        if (!current?.favoriteId || !current?.role) {
            return;
        }

        const result = this.favoriteMenuService.toggleFavorite({
            favoriteId: current.favoriteId,
            role: current.role,
            label: current.label,
            route: current.url,
        });

        this.favoriteIcon = result.added ? 'pi pi-fw pi-star-fill' : 'pi pi-fw pi-star';

        const event: BreadcrumbEventModel = {
            topic: current.favoriteId,
            payload: {
                add: result.added,
                remove: result.removed,
                menu: result.favorito,
                username: this.sessionService.getInstance()?.user?.username,
            },
        };
        this.service.publish(event);

        if (result.added) {
            this.messagesUtils.showMessageSuccessToast('messages.success.favoritePageInsert');
        } else if (result.removed) {
            this.messagesUtils.showMessageSuccessToast('messages.success.favoritePageRemove');
        }
    }

    helpOnAction() {
        const cardInfoId = hasItemsGreaterThanZero(this.listBreadcrumbs)
            ? this.listBreadcrumbs[this.listBreadcrumbs.length - 1].cardInfoId
            : null;
        this.cookieService.delete(this.sessionService.createNameCookieWithDomain(cardInfoId));
        const event: BreadcrumbEventModel = {
            topic: cardInfoId,
            payload: { show: true }
        }
        this.service.publish(event);
    }

    private getCurrentBreadcrumb(): Breadcrumb | undefined {
        return hasItemsGreaterThanZero(this.listBreadcrumbs)
            ? this.listBreadcrumbs[this.listBreadcrumbs.length - 1]
            : undefined;
    }

    getInitialStateFavoriteIcon(): string {
        const favoriteId = this.getCurrentBreadcrumb()?.favoriteId;
        return this.favoriteMenuService.isFavorite(favoriteId)
            ? 'pi pi-fw pi-star-fill'
            : 'pi pi-fw pi-star';
    }
}

interface Breadcrumb {
    label: string;
    url?: string;
    favoriteId?: string;
    cardInfoId?: string;
    role?: string;
}
